import { ReactNode, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import style from "./rule-editable-value.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { useOnClickOutside } from "usehooks-ts";
import classNames from "classnames";
import { getPathPrefix } from "utils/getPathPrefix";
import { PortalID } from "frontend/modal/portal-types";
import { clamp } from "utils/math-utils";

interface RuleEditableContainer<T> {
  value?: T;
  placeholder: string;
  getTitle: (value: T) => ReactNode;
  onChange: (value: T) => void;
  editable: boolean;
  variant?: "field" | "item";
  name?: string;
  isMissing?: boolean;
}

interface RuleEditableDropdown<T> extends RuleEditableContainer<T> {
  options: T[];
}

interface RuleEditableText {
  value?: string;
  placeholder: string;
  onChange: (value: string) => void;
  editable: boolean;
  name?: string;
}

function RuleEditableContainer<T>({
  value,
  placeholder,
  getTitle,
  children,
  editable,
  variant = "field",
  name,
  isMissing = false,
}: RuleEditableContainer<T> & { children: any }) {
  const ref = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // @ts-expect-error usehooks-ts clais to only accept mousedown or mouseup but click works fine.
  useOnClickOutside(ref, () => setDropdownOpen(false), "click");

  const portal = document.getElementById(PortalID.Floaters);

  function renderDropdown() {
    const element = ref.current ? (
      <Floater relativeTo={ref} boundary="stage-container" side="bottom" margin={-4}>
        {children}
      </Floater>
    ) : null;

    return portal ? ReactDOM.createPortal(element, portal) : element;
  }

  if (typeof children === "function") {
    // allow children to be a function that returns the dropdown content
    children = children(setDropdownOpen);
  }

  function renderChevron() {
    return (
      <svg width="8" height="5" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.99982 1L3 3.05226" stroke="currentColor" strokeLinecap="round" />
        <path d="M1 1L2.99982 3.05226" stroke="currentColor" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <div
      className={classNames(style.container, {
        [style.field]: variant === "field",
        [style.item]: variant === "item",
      })}
      ref={ref}
      style={{ zIndex: dropdownOpen ? 11 : undefined }}
    >
      <div
        className={classNames(style.value, {
          [style.disabled]: !editable,
          [style.field]: variant === "field",
          [style.item]: variant === "item",
          [style.placeholder]: !value,
          [style.missing]: isMissing,
        })}
        data-name={name}
        onClick={() => setDropdownOpen(editable && !dropdownOpen)}
      >
        <span>{value ? getTitle(value) : placeholder}</span>
        {variant === "item" && renderChevron()}
      </div>
      {dropdownOpen && renderDropdown()}
    </div>
  );
}

export function RuleEditableDropdown<T>(props: RuleEditableDropdown<T>) {
  const { value, options, getTitle, onChange, variant } = props;

  function renderCheckmark() {
    return (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4.41406L3.5 6.91406L9 1.41406" stroke="#4A90FC" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  function renderOption(option: T, setDropdownOpen: any) {
    const isSelected = value === option;
    return (
      <div
        key={JSON.stringify(option)}
        className={classNames(style.option, { [style.selected]: isSelected })}
        onClick={(e) => {
          e.stopPropagation();
          onChange(option);
          setDropdownOpen(false);
        }}
      >
        <span>{getTitle(option)}</span>
        {isSelected && renderCheckmark()}
      </div>
    );
  }

  return (
    <RuleEditableContainer {...props}>
      {(setDropdownOpen: any) => (
        <>
          <div className={classNames(style.dropdown, { [style.item]: variant === "item" })}>
            {options.length === 0 ? (
              <div className={style.noOptions}>No options</div>
            ) : (
              options.map((id) => renderOption(id, setDropdownOpen))
            )}
          </div>
        </>
      )}
    </RuleEditableContainer>
  );
}

export function RuleEditableText({ value, placeholder, onChange, editable, name }: RuleEditableText) {
  return (
    <input
      className={style.input}
      type="text"
      autoComplete="off"
      name={"rule-editable-text"}
      data-name={name}
      defaultValue={value}
      placeholder={placeholder}
      disabled={!editable}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "Tab") {
          // unfocus, change caller's value
          onChange(e.currentTarget.value.trim());
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.currentTarget.value = value ?? "";
          // unfocus, don't change caller's value
          e.currentTarget.blur();
        }
      }}
      onBlur={(e) => {
        onChange(e.currentTarget.value.trim());
      }}
      size={clamp(value?.length ?? 0, 5, 20)}
      onChange={(e) => {
        e.target.size = clamp(e.target.value.length, 5, 20);
      }}
    />
  );
}

enum RuleIcon {
  arrowUp = "arrow-up",
  arrowDown = "arrow-down",
  greenCheckmark = "green-checkmark",
  redX = "red-x",
}

export function RuleIconPicker({
  value,
  onChange,
  editable,
}: {
  value?: string;
  onChange: (value: string) => void;
  editable: boolean;
}) {
  const icons = Object.values(RuleIcon);

  return (
    <RuleEditableDropdown
      value={value}
      placeholder="Icon"
      options={icons}
      getTitle={(icon) => <img alt="icon" src={getPathPrefix(`/images/${icon}.svg`)} />}
      onChange={onChange}
      editable={editable}
    />
  );
}

interface RuleCategorizedDropdownProps<T> extends RuleEditableContainer<T> {
  options: { category: string; option: T }[];
  categories: Record<string, { title: string; icon?: ReactNode }>;
  valueMissing?: boolean;
}

export function RuleCategorizedDropdown<T>(props: RuleCategorizedDropdownProps<T>) {
  const { value, options, getTitle, onChange, variant, categories, valueMissing } = props;

  function renderCheckmark() {
    return (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4.41406L3.5 6.91406L9 1.41406" stroke="#4A90FC" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  function renderOption(option: T, setDropdownOpen: any) {
    const isSelected = value === option;
    return (
      <div
        key={JSON.stringify(option)}
        className={classNames(style.option, { [style.selected]: isSelected })}
        onClick={(e) => {
          e.stopPropagation();
          onChange(option);
          setDropdownOpen(false);
        }}
      >
        <span>{getTitle(option)}</span>
        {isSelected && renderCheckmark()}
      </div>
    );
  }

  const optionsByCategory = useMemo(() => {
    return options.reduce((acc, option) => {
      acc[option.category] = [...(acc[option.category] || []), option.option];
      return acc;
    }, {} as Record<string, T[]>);
  }, [options]);

  return (
    <RuleEditableContainer {...props} isMissing={valueMissing}>
      {(setDropdownOpen: any) => (
        <>
          <div className={classNames(style.dropdown, { [style.item]: variant === "item" })}>
            {Object.entries(categories).map(([id, category]) => {
              return (
                <div key={id}>
                  <div className={style.categoryTitle}>
                    {category.icon ?? null}
                    {category.title}
                  </div>
                  {!optionsByCategory[id] && <div className={style.noOptions}>No options</div>}
                  {optionsByCategory[id]?.map((option) => renderOption(option, setDropdownOpen))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </RuleEditableContainer>
  );
}
